import { useQueryClient } from '@tanstack/react-query';
import { useModals } from '@customerio/pluma-components/react';
import { useBillingEditGuard, type CreditsPurchasePageName } from '@/features/billing';
import type { AddFundsAction } from '@/features/billing-usage';
import { FlexPlanSheetModal } from '@/features/flex-plan-sheet';
import { useAuth } from '@/services/auth';
import { AccountState } from '@/services/auth/types';
import { billing } from '@/services/billing';
import { addFundsRequest } from '../components/add-funds-request';
import type { AddFundsFunding } from '../utils/add-funds';
import { FLEX_WALLET_QUERY_KEY, useFlexWallet } from './use-flex-wallet';
import { sleep } from '@/utils/sleep';

const PLAN_STATUS_UNAVAILABLE_MESSAGE = 'Credit purchases are not available for your current plan status.';

const CARD_REFRESH_ATTEMPTS = 3;
const CARD_REFRESH_DELAY_MS = 2000;

export type FlexAddFundsOptions = {
  amountDollars?: number;
  funding?: AddFundsFunding;
};

export type FlexAddFunds = {
  action: AddFundsAction;
  open: (options?: FlexAddFundsOptions) => Promise<void>;
};

function isBlockedPlanStatus(accountState: string, isPaused: boolean): boolean {
  return (
    isPaused ||
    accountState === AccountState.Canceled ||
    accountState === AccountState.Suspended ||
    accountState === AccountState.PendingCancellation
  );
}

type FlexAddFundsGating = { isDisabled: boolean; message: string; balanceCents: number; hasFunded: boolean };

function blockMessage(editGuardMessage: string, isBlocked: boolean): string {
  if (editGuardMessage !== '') return editGuardMessage;
  return isBlocked ? PLAN_STATUS_UNAVAILABLE_MESSAGE : '';
}

function useFlexAddFundsGating(): FlexAddFundsGating {
  const { account } = useAuth();
  const editGuard = useBillingEditGuard();
  const wallet = useFlexWallet();

  const balanceCents = wallet.status === 'ready' ? wallet.balanceCents : undefined;
  const isBlocked = isBlockedPlanStatus(account.account_state, account.is_paused === true);

  return {
    isDisabled: editGuard.isDisabled || balanceCents == null || isBlocked,
    message: blockMessage(editGuard.disabledMessage, isBlocked),
    balanceCents: balanceCents ?? 0,
    hasFunded: wallet.status === 'ready' && wallet.hasFunded,
  };
}

export function useFlexAddFunds(pageName: CreditsPurchasePageName): FlexAddFunds {
  const modals = useModals();
  const queryClient = useQueryClient();
  const gating = useFlexAddFundsGating();

  const refreshAfterPurchase = async (savedCard: boolean) => {
    await queryClient.invalidateQueries({ queryKey: FLEX_WALLET_QUERY_KEY });
    if (!savedCard) return;
    for (let attempt = 0; attempt < CARD_REFRESH_ATTEMPTS; attempt++) {
      await sleep(CARD_REFRESH_DELAY_MS);
      await billing.initialize.perform({ refresh: true });
    }
  };

  const open = async (options: FlexAddFundsOptions = {}): Promise<void> => {
    const result = await addFundsRequest.request({
      currentBalanceCents: gating.balanceCents,
      hasFunded: gating.hasFunded,
      pageName,
      initialAmountDollars: options.amountDollars,
      funding: options.funding,
    });
    if (result.reason === 'confirm') {
      await refreshAfterPurchase(result.data?.savedCard ?? false);
      return;
    }
    if (result.data?.viewFlexPricing === true) {
      modals.open(
        FlexPlanSheetModal,
        {
          addFunds: {
            onPurchase: () => void open(options),
            isPurchaseDisabled: gating.isDisabled,
            purchaseDisabledMessage: gating.message,
          },
        },
        { shouldCloseOnUnmount: true },
      );
    }
  };

  return {
    action: {
      onPurchase: () => void open(),
      isPurchaseDisabled: gating.isDisabled,
      purchaseDisabledMessage: gating.message,
    },
    open,
  };
}
